import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, type FormEvent, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Pencil, Plus } from "lucide-react";
import { DEFAULT_PAGE_SIZE } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import { createTariff, deleteTariff, listTariffs, updateTariff } from "@/api/tariffs";
import { ApiError } from "@/api/client";
import { type TariffListItem } from "@/api/types";

// Real implementation of Task 29 Part C, replacing the Task-22 placeholder.
// `GET /v1/tariffs` has no pagination/sort/filter support on the backend
// (bare `{ data: TariffListItem[] }`, "not paginated — small table" per its
// own doc comment) — same client-side-driven DataTable situation as the
// assets page and Task 25's merchants list.
//
// Reachable only via the "Tariffs" nav item, gated to CONFIG_MANAGER in
// _app.tsx's NAV_SECTIONS — every control on this page requires that same
// role, so no further per-control hasRole() gating is needed here.
export const Route = createFileRoute("/_app/tariffs")({
  component: TariffsPage,
});

const ERROR_BOX = "rounded-md border border-[#EF4444]/30 bg-[#EF4444]/5 px-3 py-2 text-sm text-[#EF4444]";

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

// ─── row → detail navigation ─────────────────────────────────────────────────
//
// Same "wrap every cell in a <Link>" pattern _app.merchants.tsx's RowLink
// established — DataTable has no onRowClick concept of its own.

function RowLink({ tariffId, children }: { tariffId: string; children: ReactNode }) {
  return (
    <Link to="/tariffs/$tariffId" params={{ tariffId }} className="group -m-2 block p-2">
      {children}
    </Link>
  );
}

// ─── create tariff dialog ────────────────────────────────────────────────────
//
// Mirrors _app.merchants.tsx's CreateMerchantDialog pattern exactly — a
// single required `name` field, inline error box reading ApiError.message
// verbatim (the 409 TariffsService.create() throws on a duplicate name).

function CreateTariffDialog() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName("");
    setError(null);
  };

  const createMutation = useMutation({
    mutationFn: () => createTariff({ name: name.trim() }),
    onSuccess: () => {
      toast.success("Tariff created.");
      queryClient.invalidateQueries({ queryKey: ["tariffs"] });
      setOpen(false);
      reset();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Failed to create tariff.");
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    createMutation.mutate();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4" />
          Create tariff
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create tariff</DialogTitle>
          <DialogDescription>Creates a new fee tariff with no rules yet.</DialogDescription>
        </DialogHeader>

        {error && <div className={ERROR_BOX}>{error}</div>}

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="tariff-name">Name</Label>
            <Input id="tariff-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={createMutation.isPending} className="w-full sm:w-auto">
              {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create tariff"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── rename tariff dialog ────────────────────────────────────────────────────

function RenameTariffDialog({ tariff }: { tariff: TariffListItem }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(tariff.name);
  const [error, setError] = useState<string | null>(null);

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      setName(tariff.name);
      setError(null);
    }
  };

  const mutation = useMutation({
    mutationFn: () => updateTariff(tariff.id, { name: name.trim() }),
    onSuccess: () => {
      toast.success("Tariff renamed.");
      queryClient.invalidateQueries({ queryKey: ["tariffs"] });
      setOpen(false);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Failed to rename tariff.");
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    mutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          <Pencil className="h-3.5 w-3.5" />
          Rename
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename tariff</DialogTitle>
          <DialogDescription>Updates this tariff&apos;s display name.</DialogDescription>
        </DialogHeader>

        {error && <div className={ERROR_BOX}>{error}</div>}

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="rename-tariff-name">Name</Label>
            <Input id="rename-tariff-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending} className="w-full sm:w-auto">
              {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── delete tariff (AlertDialog confirmation) ───────────────────────────────
//
// Mirrors _app.merchants.tsx's access-revoke AlertDialog exactly — a real,
// consequential action gets a confirm step. On the backend's 409 ("still
// assigned to a merchant"), the toast shows the backend's message verbatim
// and the row simply stays in the list (nothing was optimistically removed).

function DeleteTariffButton({ tariff }: { tariff: TariffListItem }) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => deleteTariff(tariff.id),
    onSuccess: () => {
      toast.success("Tariff deleted.");
      queryClient.invalidateQueries({ queryKey: ["tariffs"] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "Failed to delete tariff.");
    },
  });

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button type="button" variant="destructive" size="sm" disabled={mutation.isPending}>
          {mutation.isPending ? "Deleting…" : "Delete"}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete &ldquo;{tariff.name}&rdquo;?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently removes the tariff and all of its per-asset rules. Merchants currently
            assigned to it must be reassigned first — deletion is refused while any merchant still uses it.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => mutation.mutate()}>Delete tariff</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─── tariffs page ────────────────────────────────────────────────────────────

interface ListState {
  page: number;
  pageSize: number;
}

function TariffsPage() {
  const { data, isPending, isError } = useQuery({
    queryKey: ["tariffs"],
    queryFn: listTariffs,
  });

  const [state, setState] = useState<ListState>({ page: 1, pageSize: DEFAULT_PAGE_SIZE });

  const allItems = data?.data ?? [];
  const total = allItems.length;
  const pageItems = allItems.slice((state.page - 1) * state.pageSize, state.page * state.pageSize);

  const columns: DataTableColumn<TariffListItem>[] = [
    {
      key: "name",
      header: "Name",
      cell: (t) => (
        <RowLink tariffId={t.id}>
          <span className="group-hover:underline">{t.name}</span>
        </RowLink>
      ),
    },
    {
      key: "publicId",
      header: "Public ID",
      cell: (t) => (
        <RowLink tariffId={t.id}>
          <span className="font-mono text-xs text-muted-foreground group-hover:underline">{t.publicId}</span>
        </RowLink>
      ),
    },
    {
      key: "ruleCount",
      header: "Rules",
      cell: (t) => (
        <RowLink tariffId={t.id}>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{t.ruleCount}</span>
        </RowLink>
      ),
    },
    {
      key: "merchantCount",
      header: "Merchants",
      cell: (t) => (
        <RowLink tariffId={t.id}>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{t.merchantCount}</span>
        </RowLink>
      ),
    },
    {
      key: "createdAt",
      header: "Created",
      cell: (t) => (
        <RowLink tariffId={t.id}>
          <span className="text-muted-foreground">{formatTimestamp(t.createdAt)}</span>
        </RowLink>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      className: "text-right",
      cell: (t) => (
        <div className="flex items-center justify-end gap-2">
          <RenameTariffDialog tariff={t} />
          <DeleteTariffButton tariff={t} />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <CreateTariffDialog />
      </div>

      <DataTable
        columns={columns}
        items={pageItems}
        total={total}
        page={state.page}
        pageSize={state.pageSize}
        loading={isPending}
        error={isError ? "Failed to load tariffs." : null}
        onPageChange={(page) => setState((s) => ({ ...s, page }))}
        onPageSizeChange={(pageSize) => setState((s) => ({ ...s, pageSize, page: 1 }))}
        getRowKey={(t) => t.id}
        emptyMessage="No tariffs yet."
      />
    </div>
  );
}
